import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import useTheme from "@mui/material/styles/useTheme";
import { submitWriter } from "csi/service/writerService";
import { sessionStore } from "csi/state/rootStore";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { TRANSPARENT, TransparentSwatch, cssCheckeredBackground } from "designSystem/components/colorPicker/ColorPicker";
import { Box, ButtonMenu, ColorPicker } from "designSystem/index";
import { grey, white } from "designSystem/styles/color";
import { debounce, isEqual, without } from "lodash";
import { action, computed, makeAutoObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import React, { useEffect } from "react";
import { DEFAULT_BACKGROUND_COLOR } from "../snapshotTemplates/snapshotUtils";

const MAX_RECENT_COLORS = 10;
const CIRCLE_DEFAULT_COLORS = [
	TRANSPARENT, "#FFFFFFFF", "#EEEEEEFF", "#D9D9D9FF", "#969696FF", "#525252FF", "#363636FF", "#000000FF",
	"#FFCDD3FF", "#F8BAD0FF", "#FFE0B1FF", "#FFF9C4FF", "#DCECC8FF", "#B2EAF2FF", "#BBDEFBFF", "#E0BEE7FF",
	"#E47373FF", "#F06292FF", "#FFB74DFF", "#FFF076FF", "#AED580FF", "#4ED0E1FF", "#64B5F6FF", "#BA68C8FF",
	"#F44336FF", "#E91E62FF", "#FF9800FF", "#FFEB3BFF", "#8BC24BFF", "#00BCD4FF", "#2196F3FF", "#9D27B0FF",
	"#D3302FFF", "#C2175BFF", "#F47D02FF", "#FBBF2DFF", "#679F37FF", "#0097A7FF", "#1876D1FF", "#7B1EA2FF",
	"#B71D1CFF", "#870E4FFF", "#E65000FF", "#F57F16FF", "#33691DFF", "#035F64FF", "#0D47A1FF", "#4A148CFF",
];

type BackgroundColorPickerCallback = (color: string) => void;
type BackgroundColorPickerCallbackForBGColor = (color: string) => Promise<void>;

interface BackgroundColorPickerProps {
	/** callback function to call when color picker picks a new color */
	onChange?: BackgroundColorPickerCallbackForBGColor;
	
	/** callback function to call when color picker is closed */
	onClose?: BackgroundColorPickerCallback;

	/** preset background color from template selection */
	presetBackgroundColor?: string;

	/** Flag for when the viewer reset option is active */
	resetBackgroundColorToDefault?: boolean;
}

class LocalColorPickerStore {
	/** Callback for the template store so that the background color can be validated against the image format */
	readonly onClose: BackgroundColorPickerCallback;
	
	/** Callback for when the color picker selects a new color */
	readonly onChange: BackgroundColorPickerCallbackForBGColor;

	constructor(onClose: BackgroundColorPickerCallback, onChange?: BackgroundColorPickerCallbackForBGColor) {
		makeAutoObservable(this);
		this.onClose = onClose;
		this.onChange = onChange;

		this.initRecentCustomColors();
	}

	/** background color that's been selected */
	@observable backgroundColor = DEFAULT_BACKGROUND_COLOR;

	/** is the color picker menu open */
	@observable colorPickerMenuOpen = false;

	/** Force update on page change */
	@observable forceColorPickerUpdate = false;

	/** Recent custom colors of the color picker */
	@observable.ref recentCustomColors: string[] = [];

	/** get the recently used background colors */
	@computed get userPreferencesRecentCustomColors(): any {
		return sessionStore.userPreferences.ThreeDColorPickerCustomColors || [];
	}

	/** add a recently used background color to the list of recent colors */
	@action addRecentColor = async (color: string): Promise<void> => {
		const recentColor = color.substring(0, 1) === "#" ? color.toUpperCase() : color;
		let allRecentColors;

		if (this.isDefaultColor(recentColor)) {
			return;
		}       

		const colors = [...this.userPreferencesRecentCustomColors];
		allRecentColors = [recentColor, ...without(colors, recentColor)];
		if (allRecentColors.length > MAX_RECENT_COLORS) {
			allRecentColors = allRecentColors.slice(0, MAX_RECENT_COLORS);
		}

		// if all recent colors is less than 9, fill the rest with #fff
		if (allRecentColors.length < MAX_RECENT_COLORS) {
			allRecentColors = allRecentColors.concat(Array(MAX_RECENT_COLORS - allRecentColors.length)
				.fill("#ffffff"));
		}

		this.recentCustomColors = allRecentColors;
		await this.debouncedSetRecentCustomColors(recentColor);
	};

	/** Return true if current color is a default color */
	isDefaultColor = (color: string): boolean => {
		return CIRCLE_DEFAULT_COLORS.includes(color);
	};

	/** set a debounce to reduce the calls to set recent color */
	debouncedSetRecentCustomColors = debounce(async (recentColor) => {
		await this.setRecentCustomColors(recentColor);
	}, 300);

	/** set the recently used background colors */
	@action private setRecentCustomColors = async (recentColor): Promise<void> => {
		const { userPreferences } = sessionStore;	
		const { recentCustomColors, userPreferencesRecentCustomColors } = this;

		if (!isEqual(recentCustomColors, userPreferencesRecentCustomColors)) {
			const writer = new Writer();
			const cn = writer.changeNode(userPreferences.$URL);
			cn.changeAttribute("ThreeDColorPickerCustomColors", xml.ATTR_STRINGVECTOR, recentCustomColors);
			try {
				await submitWriter(writer);
			} catch (e) {
				console.error(e);
			}
		}
	};

	/** set force update */
	@action setForceColorPickerUpdate = () => {
		this.forceColorPickerUpdate = !this.forceColorPickerUpdate;
	};

	/** set the background color */
	@action setBackgroundColor = (color: string) => {
		this.backgroundColor = color;
	};

	/** set the background color, add to recent color and call the callback */
	@action selectBackgroundColor = (color: string) => {
		this.setBackgroundColor(color);
		this.addRecentColor(color);
		this.onChange?.(color);
	};

	/** open the color picker menu */
	@action openColorPickerMenu = (open: boolean) => {
		this.colorPickerMenuOpen = open;

		if (!open) {
			this.onClose?.(this.backgroundColor);
		}
	};

	/** initialize the recent custom colors */
	@action initRecentCustomColors = (): void => {
		const { ThreeDColorPickerCustomColors } = sessionStore.userPreferences;
		let recentColors = ThreeDColorPickerCustomColors || [];
		//if there is less than 9 colors, fill the rest with #fff
		if (recentColors.length < MAX_RECENT_COLORS) {
			recentColors = recentColors.concat(Array(MAX_RECENT_COLORS - recentColors.length)
				.fill("#ffffff"));
		}
		this.recentCustomColors = recentColors;
	};
}

const LocalBackgroundColorPickerStoreContext = React.createContext<LocalColorPickerStore>(null);
const useLocalBackgroundColorPickerStore = () => React.useContext(LocalBackgroundColorPickerStoreContext);

const ColorSwatch = observer(() => {
	const { palette } = useTheme();	
	const primary = palette.primary.main;
	const { backgroundColor, colorPickerMenuOpen } = useLocalBackgroundColorPickerStore();
	const styles = {
		background: (backgroundColor === "transparent") ? "#FFF" : `linear-gradient(${backgroundColor}, ${backgroundColor}), ${cssCheckeredBackground}`,
	};

	return (
		<>
			<Box 
				border="1px solid rgba(0, 0, 0, 0.1)"
				borderRadius="3px"
				height={15} 
				style={styles}
				width={15}
			/>
			{backgroundColor === "transparent" && (
				<TransparentSwatch
					sx={{
						left: "3px",
						top: "11px",
					}}
				/>
			)}
			<ArrowDropDownIcon 
				sx={{
					color: colorPickerMenuOpen ? primary : grey[800],
					fontSize: "15px !important",
					left: 16,
					position: "absolute",
					top: 16,
					transform: "rotate(315deg)",
				}}
			/>
		</>
	);
});

const BackgroundColorPicker = observer((props: BackgroundColorPickerProps) => {
	const { onChange, onClose, presetBackgroundColor, resetBackgroundColorToDefault } = props;
	const localStore = useLocalObservable(() => new LocalColorPickerStore( onClose, onChange ));
	const { 
		backgroundColor, 
		colorPickerMenuOpen, 
		forceColorPickerUpdate,
		openColorPickerMenu, 
		setBackgroundColor,
		selectBackgroundColor, 
		setForceColorPickerUpdate,
		recentCustomColors,
	} = localStore;

	useEffect(() => {
		if (presetBackgroundColor) {
			setBackgroundColor(presetBackgroundColor);
		}
	}, [presetBackgroundColor]);

	useEffect(() => {
		setBackgroundColor(DEFAULT_BACKGROUND_COLOR);
	}, [resetBackgroundColorToDefault]);

	const { palette } = useTheme();	
	const secondary = palette.NavListItemSelectedBG;

	return (
		<LocalBackgroundColorPickerStoreContext.Provider value={localStore}>
			<ButtonMenu 
				endIcon={(
					<ColorSwatch />
				)}
				menuProps={{
					anchorOrigin: {
						vertical: -4,
						horizontal: 35,
					},
					transformOrigin: {
						vertical: "top",
						horizontal: "left",
					},
				}}
				onClose={() => openColorPickerMenu(false)}
				onOpen={() => openColorPickerMenu(true)}
				size="medium"
				sx={{
					width: "26px",
					backgroundColor: colorPickerMenuOpen ? secondary : white,
				}}
				tooltipProps={
					{
						placement: "right",
						res: "BackgroundColor",                   
					}
				}
			>
				<ColorPicker
					color={backgroundColor}
					defaultColors={CIRCLE_DEFAULT_COLORS}
					displayAlpha
					forceColorPickerUpdate={forceColorPickerUpdate}
					onColorChange={selectBackgroundColor}
					onPageChange={setForceColorPickerUpdate} 
					recentCustomColors={recentCustomColors}  
				/>
			</ButtonMenu>
		</LocalBackgroundColorPickerStoreContext.Provider>
	);
});
export default BackgroundColorPicker;